/**
 * 運用詳細 — 見える化データタブ
 *
 * 10センサーの現在値をStatisticカード2カラムGridで表示する。
 * APIから取得したデータをPropsで受け取り、ローディング/エラー/null値に対応する。
 */

import React from 'react';
import { Card, Row, Col, Statistic, Spin, Alert, Empty } from 'antd';
import { GRID_CONFIG, RESPONSIVE_STYLES } from '@/shared/constants/breakpoints';
import { STATISTIC_COLORS } from '@/shared/constants/colors';
import type { RegisterDisplayItem } from '@/features/panels/constants/panelDetailTab.constants';

// ========== 定数 ==========

const NULL_VALUE_PLACEHOLDER = '--';

// ========== Props ==========

interface VisualizationDataTabProps {
  readonly isMobile: boolean;
  readonly visualizationDisplayItems: readonly RegisterDisplayItem[];
  readonly isLoadingVisualization: boolean;
  readonly visualizationErrorMessage: string | null;
}

// ========== コンポーネント ==========

export const VisualizationDataTab: React.FC<VisualizationDataTabProps> = ({
  isMobile,
  visualizationDisplayItems,
  isLoadingVisualization,
  visualizationErrorMessage,
}) => {
  const colProps = isMobile
    ? GRID_CONFIG.detailView.mobile
    : GRID_CONFIG.detailView.desktop;

  const responsiveGutter = isMobile
    ? RESPONSIVE_STYLES.gutter.mobile
    : RESPONSIVE_STYLES.gutter.desktop;

  if (isLoadingVisualization) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip="見える化データを読み込み中..." />
      </div>
    );
  }

  if (visualizationErrorMessage !== null) {
    return (
      <Alert
        message="データ取得エラー"
        description={visualizationErrorMessage}
        type="warning"
        showIcon
      />
    );
  }

  if (visualizationDisplayItems.length === 0) {
    return <Empty description="送信データがありません" />;
  }

  return (
    <Row gutter={responsiveGutter}>
      {visualizationDisplayItems.map((item) => (
        <Col key={item.name} {...colProps} className="mb-2">
          <Card>
            <Statistic
              title={item.name}
              value={item.value !== null ? item.value : NULL_VALUE_PLACEHOLDER}
              precision={item.value !== null ? item.precision : undefined}
              suffix={item.value !== null ? item.unit : ''}
              valueStyle={{ color: STATISTIC_COLORS.TOTAL }}
            />
          </Card>
        </Col>
      ))}
    </Row>
  );
};
